import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, FirmwareInstructionDoc, FirmwareInstructionsService, UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { take } from "rxjs/operators";
import { FirmwareInstructionFormComponent } from "./firmware-instruction-form.component";

type FirmwareInstructionListDoc = FirmwareInstructionDoc & { _metadata: Array<{ title: string; value: string }> };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-firmware-instructions-manager",
  templateUrl: "firmware-instructions-manager.component.html",
  standalone: false,
})
export class FirmwareInstructionsManagerComponent extends BaseComponent implements OnInit {
  @ViewChild("deleteConfirmModal") deleteConfirmModal: TemplateRef<any>;

  public loading = false;
  public docs: FirmwareInstructionListDoc[] = [];
  public docToDelete: FirmwareInstructionDoc = null;
  public deleting = false;
  private deleteModalRef: NgbModalRef;

  constructor(
    userService: UserService,
    public appService: AppService,
    private firmwareInstructionsService: FirmwareInstructionsService,
    private modalService: NgbModal
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loadDocs();
  }

  loadDocs() {
    this.loading = true;
    this.firmwareInstructionsService.list().subscribe({
      next: (docs) => {
        this.docs = (docs || []).map((doc) => this.enrichDoc(doc));
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.appService.alertError(error);
      },
    });
  }

  private enrichDoc(doc: FirmwareInstructionDoc): FirmwareInstructionListDoc {
    const titles = (doc.products || []).map((product) => product.title || `Product ${product.id}`);
    const productsValue = titles.length === 0 ? String(doc.productIds?.length || 0) : titles.length <= 2 ? titles.join(", ") : `${titles.slice(0, 2).join(", ")} +${titles.length - 2}`;
    return {
      ...doc,
      _metadata: [
        { title: "Mode", value: doc.mode },
        { title: "Products", value: productsValue },
      ],
    };
  }

  createDoc() {
    const modalRef = this.appService.showModal(FirmwareInstructionFormComponent, { size: "lg" });
    modalRef.componentInstance.saved.pipe(take(1)).subscribe(() => this.loadDocs());
  }

  editDoc(doc: FirmwareInstructionDoc) {
    const modalRef = this.appService.showModal(FirmwareInstructionFormComponent, { size: "lg" });
    modalRef.componentInstance.doc = doc;
    modalRef.componentInstance.saved.pipe(take(1)).subscribe(() => this.loadDocs());
  }

  deleteDoc(doc: FirmwareInstructionDoc) {
    this.docToDelete = doc;
    this.deleteModalRef = this.modalService.open(this.deleteConfirmModal, { size: "sm", centered: true });
  }

  confirmDelete() {
    if (!this.docToDelete || this.deleting) return;
    this.deleting = true;
    this.firmwareInstructionsService.delete(this.docToDelete.id).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteModalRef?.close();
        this.docToDelete = null;
        this.loadDocs();
      },
      error: (error) => {
        this.deleting = false;
        this.appService.alertError(error);
      },
    });
  }

  cancelDelete() {
    this.deleteModalRef?.close();
    this.docToDelete = null;
  }
}
